import { useEffect } from 'react'
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import LoginPage from '@/pages/auth/LoginPage'
import AppLayout from '@/components/layout/AppLayout'
import DashboardHomePage from '@/pages/dashboard/DashboardHomePage'
import TagJourneyPage from '@/pages/dashboard/TagJourneyPage'
import PartyJourneyPage, {
  SenderJourneyPage,
  ReceiverJourneyPage,
} from '@/pages/dashboard/PartyJourneyPage'
import CurrentStatePage from '@/pages/dashboard/CurrentStatePage'
import MovementsPage from '@/pages/dashboard/MovementsPage'
import MovementDocumentPage from '@/pages/dashboard/MovementDocumentPage'
import ReportsPage from '@/pages/dashboard/ReportsPage'
import C2C1MappingPage from '@/pages/dashboard/C2C1MappingPage'
import DraftUsersPage from '@/pages/admin/DraftUsersPage'
import RfidTagsPage from '@/pages/admin/RfidTagsPage'
import { RfidEngineAdminRoute } from '@/components/auth/RfidEngineAdminRoute'
import ValidationReportPage from '@/pages/dashboard/ValidationReportPage'

function LifecycleRedirect() {
  const [params] = useSearchParams()
  const code = params.get('code')
  return (
    <Navigate
      to={code ? `/tag-journey?code=${encodeURIComponent(code)}` : '/tag-journey'}
      replace
    />
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function AppRoutes() {
  const { isLoading, isAuthenticated, validateSession } = useAuthStore()

  useEffect(() => {
    void validateSession()
  }, [validateSession])

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spin size="large" tip="Validating session…" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardHomePage />} />
        <Route path="movement-document" element={<MovementDocumentPage />} />
        <Route path="tag-journey" element={<TagJourneyPage />} />
        <Route path="party-journey" element={<PartyJourneyPage />} />
        <Route path="lifecycle" element={<LifecycleRedirect />} />
        <Route path="current-state" element={<CurrentStatePage />} />
        <Route path="movements" element={<MovementsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="c2-c1-mapping" element={<C2C1MappingPage />} />
        <Route path="validation-report" element={<ValidationReportPage />} />
        <Route path="admin/draft-users" element={<DraftUsersPage />} />
        <Route
          path="admin/rfid-tags"
          element={
            <RfidEngineAdminRoute>
              <RfidTagsPage />
            </RfidEngineAdminRoute>
          }
        />
        {/* Legacy redirects */}
        <Route path="docket-journey" element={<Navigate to="/movement-document" replace />} />
        <Route path="sender-journey" element={<SenderJourneyPage />} />
        <Route path="receiver-journey" element={<ReceiverJourneyPage />} />
      </Route>
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default function App() {
  return <AppRoutes />
}
